import React, { useState, useEffect } from 'react';
import { Authorization, UserRole, Client } from '../types';
import {
  Anchor,
  Plus,
  RefreshCw,
  FileCheck,
  AlertTriangle,
  Upload,
  CheckCircle2,
  X,
  Clock,
  ShieldCheck,
  Search
} from 'lucide-react';
import { WellSkyIngestionModal } from './WellSkyIngestionModal';
import { WellSkySyncBanner } from './WellSkySyncBanner';

interface AuthorizationsViewProps {
  userRole: UserRole;
  onSelectClient: (clientId: string) => void;
  onRefreshStats: () => void;
  onNavigateToConnections?: () => void;
  initialAction?: 'wellsky' | 'upload' | 'new' | null;
  onClearInitialAction?: () => void;
}

export const AuthorizationsView: React.FC<AuthorizationsViewProps> = ({
  userRole,
  onSelectClient,
  onRefreshStats,
  onNavigateToConnections,
  initialAction,
  onClearInitialAction
}) => {
  const [auths, setAuths] = useState<Authorization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'SEA' | 'STA' | 'FEA' | 'FTA' | 'UNMAPPED'>('ALL');

  // WellSky Production Import Modal State
  const [showWellSkyModal, setShowWellSkyModal] = useState(false);

  // New Auth Modal
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => {
    if (initialAction === 'wellsky' || initialAction === 'upload') {
      setShowWellSkyModal(true);
      onClearInitialAction?.();
    } else if (initialAction === 'new') {
      setShowNewModal(true);
      onClearInitialAction?.();
    }
  }, [initialAction, onClearInitialAction]);
  const [newAuthForm, setNewAuthForm] = useState({
    client_id: '',
    payer_name: 'Arizona Medicaid (Mercy Care)',
    auth_number: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
    service_code: '92507',
    auth_type: 'STA',
    service_line: 'SPEECH',
    total_units: 32,
    frequency: '2x/week',
    source: 'Manual Entry'
  });

  const isClinician = userRole === 'ASSISTANT';

  const parseWellSkyInput = (text: string): any[] => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        throw new Error('Invalid JSON format');
      }
    }
    // Parse CSV or Tab Delimited lines
    const lines = trimmed.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const delimiter = lines[0].includes('\t') ? '\t' : ',';
    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/[\s\/_]+/g, ''));

    const records: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(delimiter).map(c => c.trim().replace(/^["']|["']$/g, ''));
      if (cols.length === 0 || !cols.some(Boolean)) continue;
      const obj: any = {};
      headers.forEach((h, idx) => {
        const val = cols[idx] || '';
        if (h.includes('auth') || h.includes('ref')) obj.auth_number = val;
        else if (h.includes('client') || h.includes('member') || h.includes('name')) obj.client_name = val;
        else if (h.includes('policy') || h.includes('id')) obj.policy_id = val;
        else if (h.includes('code') || h.includes('cpt') || h.includes('service')) obj.service_code = val;
        else if (h.includes('start') || h.includes('effective')) obj.start_date = val;
        else if (h.includes('end') || h.includes('expir')) obj.end_date = val;
        else if (h.includes('total') || h.includes('units')) {
          if (!obj.total_units) obj.total_units = parseInt(val) || 32;
        } else if (h.includes('used')) obj.units_used = parseInt(val) || 0;
        else if (h.includes('freq')) obj.frequency = val;
        else if (h.includes('payer')) obj.payer_name = val;
      });
      if (obj.auth_number || obj.client_name) {
        records.push(obj);
      }
    }
    return records;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [authRes, cliRes] = await Promise.all([
        fetch('/api/authorizations'),
        fetch('/api/clients')
      ]);
      if (authRes.ok) setAuths(await authRes.json());
      if (cliRes.ok) setClients(await cliRes.json());
    } catch (err) {
      console.error('Error fetching authorizations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAuthForm.client_id || !newAuthForm.auth_number) return;

    try {
      const res = await fetch('/api/authorizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAuthForm)
      });
      if (res.ok) {
        setShowNewModal(false);
        await fetchData();
        onRefreshStats();
      }
    } catch (err) {
      console.error('Create authorization failed:', err);
    }
  };

  const seaCount = auths.filter(a => (a.auth_type || a.focus_service_code)?.toUpperCase() === 'SEA').length;
  const staCount = auths.filter(a => (a.auth_type || a.focus_service_code)?.toUpperCase() === 'STA').length;
  const feaCount = auths.filter(a => (a.auth_type || a.focus_service_code)?.toUpperCase() === 'FEA').length;
  const ftaCount = auths.filter(a => (a.auth_type || a.focus_service_code)?.toUpperCase() === 'FTA').length;
  const unmappedCount = auths.filter(a => {
    const code = (a.auth_type || a.focus_service_code)?.toUpperCase();
    return !code || code === 'UNMAPPED' || !['SEA', 'STA', 'FEA', 'FTA'].includes(code);
  }).length;

  const filteredAuths = auths.filter((a) => {
    const rawAuthType = (a.auth_type || a.focus_service_code || '').toUpperCase();
    const matchesSearch = `${a.auth_number} ${a.first_name || ''} ${a.last_name || ''} ${a.payer_name || ''} ${a.service_code || ''} ${rawAuthType} ${a.service_line || ''}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesType =
      filterType === 'ALL' ||
      (filterType === 'SEA' && rawAuthType === 'SEA') ||
      (filterType === 'STA' && rawAuthType === 'STA') ||
      (filterType === 'FEA' && rawAuthType === 'FEA') ||
      (filterType === 'FTA' && rawAuthType === 'FTA') ||
      (filterType === 'UNMAPPED' && (!rawAuthType || rawAuthType === 'UNMAPPED' || !['SEA', 'STA', 'FEA', 'FTA'].includes(rawAuthType)));
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-4">
      <div className="flex sm:hidden flex-col gap-2 pb-2 border-b border-slate-200">
        <div className="flex items-center space-x-2.5 min-w-0 flex-wrap">
          <h1 className="text-base font-bold text-slate-900 tracking-tight flex items-center space-x-2 shrink-0">
            <span>WellSky Import</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 leading-none">
              Unit State Machine
            </span>
          </h1>
          <span className="text-[11px] text-slate-500 font-mono whitespace-nowrap overflow-hidden text-ellipsis">
            Production export | import | reconciliation | SQL • Real identity • Zero fabrication
          </span>
        </div>

        <div className="flex items-center space-x-2 shrink-0 self-end">
          {!isClinician && (
            <>
              <button
                onClick={() => setShowWellSkyModal(true)}
                className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 text-xs font-semibold flex items-center space-x-1.5 shadow-xs transition-colors cursor-pointer"
                title="Manual file upload backup / debug"
              >
                <Upload className="w-3.5 h-3.5 text-slate-500" />
                <span>Manual Upload (Backup)</span>
              </button>
              <button
                onClick={() => {
                  if (clients.length > 0) setNewAuthForm({ ...newAuthForm, client_id: clients[0].id });
                  setShowNewModal(true);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center space-x-1.5 border border-slate-700 shadow-xs transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Authorization</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="hidden sm:flex items-center justify-between gap-4 rounded-xl border border-slate-200/90 bg-white px-4 py-3 shadow-xs">
        <div className="flex items-center space-x-3 min-w-0">
          <h1 className="text-lg font-bold text-slate-900 tracking-tight flex items-center space-x-2.5 shrink-0">
            <span>WellSky Import</span>
            <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 leading-none">
              Unit State Machine
            </span>
          </h1>
          <span className="text-xs text-slate-500 font-mono whitespace-nowrap overflow-hidden text-ellipsis">
            Production export | import | reconciliation | SQL • Real identity • Zero fabrication
          </span>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {!isClinician && (
            <>
              <button
                onClick={() => setShowWellSkyModal(true)}
                className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 text-xs font-semibold flex items-center space-x-1.5 shadow-xs transition-colors cursor-pointer"
                title="Manual file upload backup / debug"
              >
                <Upload className="w-3.5 h-3.5 text-slate-500" />
                <span>Manual Upload (Backup)</span>
              </button>
              <button
                onClick={() => {
                  if (clients.length > 0) setNewAuthForm({ ...newAuthForm, client_id: clients[0].id });
                  setShowNewModal(true);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg flex items-center space-x-1.5 shadow-xs border border-slate-700 transition-all cursor-pointer active:scale-98"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Authorization</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* WellSky Operational Sync Status & Retrieval Banner */}
      {!isClinician && (
        <WellSkySyncBanner
          onSyncComplete={() => {
            fetchData();
            onRefreshStats();
          }}
          onOpenManualModal={() => setShowWellSkyModal(true)}
          onNavigateToSettings={onNavigateToConnections}
        />
      )}

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2.5">
        <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border border-slate-200/90 shadow-xs w-full lg:w-96 shrink-0 transition-all focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search records..."
            className="w-full bg-transparent text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none tracking-tight font-medium"
          />
          {search.trim().length > 0 && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="p-0.5 text-slate-400 hover:text-slate-700 rounded transition-colors shrink-0 cursor-pointer"
              title="Clear search"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-white border border-slate-200/90 rounded-xl shadow-xs">
          <button
            onClick={() => setFilterType('ALL')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'ALL'
                ? 'bg-slate-800 text-white border border-slate-700 shadow-xs'
                : 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
            }`}
          >
            <span>All</span>
            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full border ${
              filterType === 'ALL'
                ? 'bg-white/10 text-slate-100 border-white/20'
                : 'bg-slate-50 text-slate-600 border-slate-200'
            }`}>
              {auths.length}
            </span>
          </button>

          <button
            onClick={() => setFilterType('SEA')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'SEA'
                ? 'bg-indigo-50 text-indigo-900 border border-indigo-200 shadow-xs'
                : 'bg-white text-slate-600 hover:text-indigo-800 hover:bg-indigo-50/60 border border-transparent'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
            <span>SEA • Eval</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200">
              {seaCount}
            </span>
          </button>

          <button
            onClick={() => setFilterType('STA')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'STA'
                ? 'bg-sky-50 text-sky-900 border border-sky-200 shadow-xs'
                : 'bg-white text-slate-600 hover:text-sky-800 hover:bg-sky-50/60 border border-transparent'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
            <span>STA</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200">
              {staCount}
            </span>
          </button>

          <button
            onClick={() => setFilterType('FEA')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'FEA'
                ? 'bg-emerald-50 text-emerald-900 border border-emerald-200 shadow-xs'
                : 'bg-white text-slate-600 hover:text-emerald-800 hover:bg-emerald-50/60 border border-transparent'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>FEA</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              {feaCount}
            </span>
          </button>

          <button
            onClick={() => setFilterType('FTA')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'FTA'
                ? 'bg-amber-50 text-amber-900 border border-amber-200 shadow-xs'
                : 'bg-white text-slate-600 hover:text-amber-800 hover:bg-amber-50/60 border border-transparent'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span>FTA</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
              {ftaCount}
            </span>
          </button>

          <button
            onClick={() => setFilterType('UNMAPPED')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === 'UNMAPPED'
                ? 'bg-rose-50 text-rose-900 border border-rose-200 shadow-xs'
                : 'bg-white text-slate-600 hover:text-rose-800 hover:bg-rose-50/60 border border-transparent'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
            <span>UNMAPPED</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
              {unmappedCount}
            </span>
          </button>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-slate-200/90 bg-white shadow-xs">
        {loading ? (
          <div className="p-8 text-center text-xs text-slate-600">Loading authorizations...</div>
        ) : filteredAuths.length === 0 ? (
          <div className="p-10 text-center text-xs text-slate-600">
            <Anchor className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="font-semibold text-slate-800">No authorizations match search.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs table-fixed">
              <thead className="bg-slate-50 text-[9.5px] uppercase font-bold text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="px-2.5 py-2 font-bold w-[16%]">Auth # / Client</th>
                  <th className="px-2.5 py-2 font-bold w-[8%]">Auth Type</th>
                  <th className="px-2.5 py-2 font-bold w-[9%]">Service Line</th>
                  <th className="px-2.5 py-2 font-bold w-[14%]">Payer & Plan</th>
                  <th className="px-2.5 py-2 font-bold w-[12%]">Validity Period</th>
                  <th className="px-2.5 py-2 font-bold w-[9%]">CPT & Freq</th>
                  <th className="px-2.5 py-2 font-bold w-[14%]">Unit Consumption</th>
                  <th className="px-2.5 py-2 font-bold w-[8%]">Status</th>
                  <th className="px-2.5 py-2 font-bold text-right w-[10%]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredAuths.map((auth) => {
                  const percentUsed = Math.min(100, Math.round((auth.units_used / (auth.total_units || 1)) * 100));
                  const rawType = (auth.auth_type || auth.focus_service_code || '').toUpperCase();
                  const isSea = rawType === 'SEA';
                  const isSta = rawType === 'STA';
                  const isFea = rawType === 'FEA';
                  const isFta = rawType === 'FTA';

                  return (
                    <tr key={auth.id} className="hover:bg-slate-50 odd:bg-white even:bg-slate-50/70 transition-colors">
                      <td className="px-2.5 py-1.5">
                        <div className="flex flex-col min-w-0">
                          <span className="font-mono text-slate-800 font-bold text-xs tracking-tight truncate">#{auth.auth_number}</span>
                          <button
                            onClick={() => onSelectClient(auth.client_id)}
                            className="font-bold text-slate-900 hover:text-slate-700 text-left text-xs transition-colors cursor-pointer leading-tight mt-0.5 truncate"
                          >
                            {auth.last_name}, {auth.first_name}
                          </button>
                        </div>
                      </td>

                      <td className="px-2.5 py-1.5">
                        {isSea ? (
                          <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-indigo-50 text-indigo-800 border border-indigo-200">
                            SEA
                          </span>
                        ) : isSta ? (
                          <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-sky-50 text-sky-800 border border-sky-200">
                            STA
                          </span>
                        ) : isFea ? (
                          <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-emerald-50 text-emerald-800 border border-emerald-200">
                            FEA
                          </span>
                        ) : isFta ? (
                          <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-amber-50 text-amber-800 border border-amber-200">
                            FTA
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase bg-rose-50 text-rose-800 border border-rose-200">
                            UNMAPPED
                          </span>
                        )}
                      </td>

                      <td className="px-2.5 py-1.5">
                        <span className="inline-flex items-center text-[9.5px] font-mono px-1.5 py-0.5 rounded font-medium uppercase bg-slate-50 text-slate-700 border border-slate-200 leading-none truncate max-w-full">
                          {auth.service_line || 'UNMAPPED'}
                        </span>
                      </td>

                      <td className="px-2.5 py-1.5">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-slate-800 leading-tight truncate" title={auth.payer_name}>
                            {auth.payer_name}
                          </div>
                          <div className="text-[9.5px] text-slate-500 font-mono leading-tight mt-0.5 truncate">
                            Source: {auth.source}
                          </div>
                        </div>
                      </td>

                      <td className="px-2.5 py-1.5">
                        <div className="text-[10.5px] font-mono text-slate-700 leading-tight">
                          <div>{auth.start_date}</div>
                          <div className="text-slate-500 text-[9.5px]">→ {auth.end_date}</div>
                        </div>
                      </td>

                      <td className="px-2.5 py-1.5">
                        <div className="font-mono text-slate-800 font-semibold text-xs leading-tight">{auth.service_code}</div>
                        <div className="text-[9.5px] text-slate-500 leading-tight mt-0.5 truncate">{auth.frequency}</div>
                      </td>

                      <td className="px-2.5 py-1.5">
                        <div className="flex justify-between items-center text-[10.5px] mb-0.5 font-mono gap-1">
                          <span className="text-slate-700 font-semibold truncate">
                            {auth.units_used} / {auth.total_units} <span className="text-slate-500 text-[9.5px]">units</span>
                          </span>
                          <span className={`text-[10px] font-bold shrink-0 ${
                            percentUsed >= 100 ? 'text-rose-700' : percentUsed > 75 ? 'text-amber-700' : 'text-slate-700'
                          }`}>
                            {percentUsed}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden border border-slate-200">
                          <div
                            className={`h-full rounded-full transition-all ${
                              percentUsed >= 100 ? 'bg-rose-500' : percentUsed > 75 ? 'bg-amber-500' : 'bg-slate-600'
                            }`}
                            style={{ width: `${percentUsed}%` }}
                          />
                        </div>
                        {auth.available_units !== undefined && (
                          <div className="text-[9.5px] font-mono text-slate-500 mt-0.5 leading-none">
                            avail: <span className="text-slate-700 font-medium">{auth.available_units}</span>
                          </div>
                        )}
                      </td>

                      <td className="px-2.5 py-1.5">
                        <span className={`text-[9.5px] font-mono px-1.5 py-0.5 rounded font-bold uppercase whitespace-nowrap ${
                          auth.status === 'ACTIVE'
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : auth.status === 'EXPIRING'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-rose-50 text-rose-800 border border-rose-200'
                        }`}>
                          {auth.status}
                        </span>
                      </td>

                      <td className="px-2.5 py-1.5 text-right">
                        <button
                          onClick={() => onSelectClient(auth.client_id)}
                          className="px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-800 hover:text-slate-950 border border-slate-200 hover:border-slate-300 text-[11px] font-bold shadow-xs transition-all cursor-pointer inline-flex items-center gap-1 active:scale-98 whitespace-nowrap"
                        >
                          <span>View Chart</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* WellSky Production Ingestion & Staging Center */}
      <WellSkyIngestionModal
        isOpen={showWellSkyModal}
        onClose={() => setShowWellSkyModal(false)}
        onSuccess={async () => {
          await fetchData();
          onRefreshStats();
        }}
        clients={clients}
      />

      {/* Add Auth Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-3">
              Add Authorization
            </h3>

            <form onSubmit={handleCreateAuth} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-600 mb-1">Client *</label>
                <select
                  required
                  value={newAuthForm.client_id}
                  onChange={(e) => setNewAuthForm({ ...newAuthForm, client_id: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.last_name}, {c.first_name} ({c.id})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-600 mb-1">Authorization Number *</label>
                <input
                  type="text"
                  required
                  value={newAuthForm.auth_number}
                  onChange={(e) => setNewAuthForm({ ...newAuthForm, auth_number: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  placeholder="e.g. AUTH-AZ-99120"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    required
                    value={newAuthForm.start_date}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, start_date: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 mb-1">End Date</label>
                  <input
                    type="date"
                    required
                    value={newAuthForm.end_date}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, end_date: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 mb-1">Service Code (CPT)</label>
                  <select
                    value={newAuthForm.service_code}
                    onChange={(e) => {
                      const service_code = e.target.value;
                      const classification: Record<string, { auth_type: string; service_line: string }> = {
                        '92523': { auth_type: 'SEA', service_line: 'EVALUATION' },
                        '92507': { auth_type: 'STA', service_line: 'SPEECH' },
                        '92508': { auth_type: 'STA', service_line: 'SPEECH' },
                        '92610': { auth_type: 'FEA', service_line: 'FEEDING' },
                        '92526': { auth_type: 'FTA', service_line: 'FEEDING' }
                      };
                      const next = classification[service_code] || { auth_type: 'UNMAPPED', service_line: 'UNMAPPED' };
                      setNewAuthForm({
                        ...newAuthForm,
                        service_code,
                        auth_type: next.auth_type,
                        service_line: next.service_line,
                        source: 'Manual Entry'
                      });
                    }}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  >
                    <option value="92507">92507 (Speech Treatment)</option>
                    <option value="92523">92523 (Speech Evaluation)</option>
                    <option value="92508">92508 (Speech Group)</option>
                    <option value="92610">92610 (Feeding Evaluation)</option>
                    <option value="92526">92526 (Feeding Treatment)</option>
                  </select>
                  <p className="mt-1 text-[10px] text-slate-500">
                    Saves as {newAuthForm.auth_type} · {newAuthForm.service_line} · Manual Entry
                  </p>
                </div>
                <div>
                  <label className="block text-slate-600 mb-1">Total Units</label>
                  <input
                    type="number"
                    required
                    value={newAuthForm.total_units}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, total_units: parseInt(e.target.value) || 32 })}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold"
                >
                  Save Authorization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
